/*
  Albemore hair quiz engine.

  Loaded by sections/hair-quiz.liquid. Reads two inline JSON blocks:
    #hair-quiz-data   {config, t, pages[], rules[], outcomes{}}  from snippets/hair-quiz-data.liquid
    #hair-quiz-config {routes, product, upsell, fallback, exitLink, logo, email}

  Shopify endpoints used: POST /cart/add.js, POST /cart/update.js, POST /contact.
  State is persisted to localStorage under config.storageKey so a refresh resumes.

  No dependencies, no framework. Vanilla on purpose: this runs on every quiz view.
*/
(function () {
  'use strict';

  var root = document.getElementById('hair-quiz');
  if (!root) return;

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  }

  var DATA = readJSON('hair-quiz-data');
  var CONF = readJSON('hair-quiz-config') || {};
  if (!DATA || !DATA.pages || !DATA.pages.length) {
    root.innerHTML = '<p class="hq-error">The quiz could not load. Please refresh the page.</p>';
    return;
  }

  var CFG = DATA.config || {};
  var T = DATA.t || {};
  var PAGES = DATA.pages;
  var BY_ID = {};
  PAGES.forEach(function (p) { BY_ID[p.id] = p; });

  // Pages that count towards the progress bar. Content, buffer and result screens do not.
  var COUNTED = PAGES.filter(function (p) { return p.type === 'question'; });

  var state = {
    current: CFG.firstPage || PAGES[0].id,
    answers: {},          // { question_id: [keys] | "value" | {shape,consistency,length} }
    history: [],          // visited page ids, for Back
    flags: {},            // { under_age: true, no_product: true }
    email: '',
    outcome: null,
    done: false
  };

  /* ---------- helpers ---------- */

  function t(key, vars) {
    var s = T[key] == null ? '' : String(T[key]);
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        s = s.split('{' + k + '}').join(vars[k]);
      });
    }
    return s;
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function el(tag, cls, html) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (html != null) node.innerHTML = html;
    return node;
  }

  function asArray(value) {
    if (value == null) return [];
    return Array.isArray(value) ? value : [value];
  }

  /* ---------- persistence ---------- */

  var STORE = CFG.storageKey || 'albemore-hair-quiz';

  function save() {
    try {
      localStorage.setItem(STORE, JSON.stringify({
        current: state.current,
        answers: state.answers,
        history: state.history,
        flags: state.flags,
        email: state.email,
        outcome: state.outcome,
        done: state.done,
        savedAt: Date.now()
      }));
    } catch (err) { /* private mode, quiz still works in memory */ }
  }

  function load() {
    try {
      var raw = localStorage.getItem(STORE);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.current || !BY_ID[parsed.current]) return null;
      // Anything older than 30 days is stale, start clean.
      if (parsed.savedAt && Date.now() - parsed.savedAt > 2592000000) return null;
      return parsed;
    } catch (err) {
      return null;
    }
  }

  function clearSaved() {
    try { localStorage.removeItem(STORE); } catch (err) { /* ignore */ }
  }

  /* ---------- skip rules ----------
     Grammar copied from the reference so the two stay comparable:
       "a>question_6"                 selected key a  -> jump to question_6
       "c>NONE:smart_buffer_final"    selected key c  -> no product, then jump
       "<18>NONE:smart_buffer_final"  age under 18    -> no product, then jump
       "0>question_11"                slider value 0  -> jump
     First matching rule wins. No match means the next page in document order.
  */

  function rulesFor(page) {
    var out = [];
    for (var i = 1; i <= 5; i++) {
      var raw = page['skip_rule_' + i];
      if (raw) out.push(String(raw));
    }
    return out;
  }

  function parseRule(raw) {
    var parts = raw.split('>');
    if (parts.length < 2) return null;
    var target = parts.slice(1).join('>');
    var noProduct = false;
    if (target.indexOf('NONE:') === 0) {
      noProduct = true;
      target = target.slice(5);
    }
    return { trigger: parts[0].trim(), target: target.trim(), noProduct: noProduct };
  }

  function ruleMatches(rule, page) {
    var answer = state.answers[page.id];

    if (rule.trigger.charAt(0) === '<') {
      // Age gate, only meaningful on a date question.
      var limit = parseInt(rule.trigger.slice(1), 10);
      var age = ageFromAnswer(answer);
      return age != null && !isNaN(limit) && age < limit;
    }

    if (page.questionType === 'slider_input') {
      return String(answer) === rule.trigger;
    }

    return asArray(answer).indexOf(rule.trigger) !== -1;
  }

  function ageFromAnswer(answer) {
    if (!answer || !answer.year) return null;
    if (!isRealDate(answer)) return null;
    var y = parseInt(answer.year, 10);
    var m = parseInt(answer.month, 10);
    var d = parseInt(answer.day, 10);
    if (!y || !m || !d) return null;
    var now = new Date();
    var age = now.getFullYear() - y;
    var beforeBirthday = now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d);
    if (beforeBirthday) age -= 1;
    return age;
  }

  // 31 February is not a date. new Date() would roll it over to 3 March without
  // complaining, so build the date and check the parts came back unchanged.
  function isRealDate(answer) {
    var y = parseInt(answer.year, 10);
    var m = parseInt(answer.month, 10);
    var d = parseInt(answer.day, 10);
    if (!y || !m || !d) return false;
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900) return false;
    var probe = new Date(y, m - 1, d);
    return probe.getFullYear() === y && probe.getMonth() === m - 1 && probe.getDate() === d;
  }

  function nextPageId(page) {
    var raws = rulesFor(page);
    for (var i = 0; i < raws.length; i++) {
      var rule = parseRule(raws[i]);
      if (!rule) continue;
      if (ruleMatches(rule, page)) {
        if (rule.noProduct) state.flags.no_product = true;
        if (rule.trigger.charAt(0) === '<') state.flags.under_age = true;
        if (BY_ID[rule.target]) return rule.target;
      }
    }
    var index = PAGES.indexOf(page);
    return index > -1 && PAGES[index + 1] ? PAGES[index + 1].id : null;
  }

  function goTo(id, pushHistory) {
    if (!BY_ID[id]) return;
    if (pushHistory !== false) state.history.push(state.current);
    state.current = id;
    save();
    render();
    var top = root.getBoundingClientRect().top + window.pageYOffset - 12;
    window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
  }

  function goNext() {
    var page = BY_ID[state.current];
    var target = nextPageId(page);
    if (target) goTo(target);
  }

  function goBack() {
    var previous = state.history.pop();
    if (!previous) return;
    state.current = previous;
    save();
    render();
  }

  function progressPercent() {
    var page = BY_ID[state.current];
    if (page.type === 'smart_buffer') return page.progress || 95;
    if (page.type === 'result' || page.type === 'upsell' || page.type === 'checkout') return 100;
    var answered = COUNTED.filter(function (q) {
      return state.answers[q.id] != null;
    }).length;
    var pct = Math.round((answered / COUNTED.length) * 90);
    return pct < 4 ? 4 : pct;
  }

  function questionNumber(page) {
    var i = COUNTED.indexOf(page);
    return i === -1 ? null : i + 1;
  }

  /* ---------- validation ---------- */

  function validate(page) {
    var answer = state.answers[page.id];

    if (page.questionType === 'email_input') {
      var email = String(answer || '').trim();
      if (!email) return t('email_required');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return t('email_invalid');
      return null;
    }

    if (page.questionType === 'date_input') {
      if (!answer || !answer.day || !answer.month || !answer.year) return t('date_required');
      if (!isRealDate(answer)) return t('date_invalid');
      var age = ageFromAnswer(answer);
      if (age == null || age < 0 || age > 120) return t('date_invalid');
      return null;
    }

    if (page.questionType === 'hair_description') {
      if (!answer || !answer.shape || !answer.consistency || !answer.length) return t('hair_required');
      return null;
    }

    if (!page.required) return null;

    if (page.questionType === 'multiple_choice') {
      return asArray(answer).length ? null : t('multiple_choice_required');
    }

    if (page.questionType === 'single_choice') {
      return asArray(answer).length ? null : t('single_choice_required');
    }

    if (page.questionType === 'slider_input') {
      return answer == null ? t('single_choice_required') : null;
    }

    return null;
  }

  function showError(message) {
    var box = root.querySelector('.hq-error-live');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  function attemptNext() {
    var page = BY_ID[state.current];
    var problem = validate(page);
    if (problem) {
      showError(problem);
      return;
    }
    showError('');
    goNext();
  }

  /* ---------- recommendation ---------- */

  function anyMatches(map) {
    var qids = Object.keys(map || {});
    for (var i = 0; i < qids.length; i++) {
      var wanted = map[qids[i]];
      var given = asArray(state.answers[qids[i]]).map(String);
      for (var j = 0; j < wanted.length; j++) {
        if (given.indexOf(String(wanted[j])) !== -1) return true;
      }
    }
    return false;
  }

  function decideOutcome() {
    var rules = DATA.rules || [];
    for (var i = 0; i < rules.length; i++) {
      var when = rules[i].when || {};
      if (when.flag) {
        if (state.flags[when.flag]) return rules[i].outcome;
        continue;
      }
      if (when.any) {
        if (anyMatches(when.any)) return rules[i].outcome;
        continue;
      }
      return rules[i].outcome; // empty when {} is the catch-all
    }
    return null;
  }

  function labelFor(qid, key) {
    var page = BY_ID[qid];
    if (!page || !page.options) return key;
    for (var i = 0; i < page.options.length; i++) {
      if (page.options[i].key === key) return page.options[i].text;
    }
    return key;
  }

  function fillTokens(text) {
    var age = ageFromAnswer(state.answers.question_7);
    var help = asArray(state.answers.question_23)
      .filter(function (k) { return k !== 'g'; })
      .map(function (k) { return labelFor('question_23', k).toLowerCase(); });
    var joined = help.length > 1
      ? help.slice(0, -1).join(', ') + ' and ' + help[help.length - 1]
      : (help[0] || '');
    return String(text || '')
      .split('{age}').join(age == null ? '' : age)
      .split('{help_items}').join(joined);
  }

  // Builds the personalised lines shown on the smart buffer and again on the result page.
  function buildInsights() {
    var page = BY_ID[CFG.bufferPage] || {};
    var groups = page.recommendationGroups || {};
    var out = [];

    Object.keys(groups).forEach(function (groupName) {
      groups[groupName].forEach(function (entry) {
        var given = asArray(state.answers[entry.q]).map(String);
        var keys = entry.keys || [];
        var hit = false;

        if (keys.indexOf('dynamic') !== -1) {
          hit = given.length > 0 || state.answers[entry.q] != null;
        } else if (keys.indexOf('any') !== -1) {
          hit = given.length > 0 && given.indexOf('g') === -1 && given.indexOf('none') === -1;
        } else {
          hit = keys.some(function (k) { return given.indexOf(String(k)) !== -1; });
        }

        if (!hit) return;
        var title = fillTokens(entry.title);
        var body = fillTokens(entry.body);
        if (!title || title === 'Age') return;
        out.push({ group: groupName, title: title, body: body });
      });
    });

    return out;
  }

  /* ---------- Shopify ---------- */

  // A readable summary of the shopper's answers, so the merchant sees it on the order
  // without needing an app. Written to cart attributes at email submit and at add to cart.
  function answerAttributes() {
    var attrs = {};
    COUNTED.forEach(function (page) {
      var answer = state.answers[page.id];
      if (answer == null) return;
      var value;
      if (page.questionType === 'date_input') {
        var age = ageFromAnswer(answer);
        value = age == null ? '' : String(age);
      } else if (page.questionType === 'hair_description') {
        value = [answer.shape, answer.consistency, answer.length].join(', ');
      } else if (page.questionType === 'slider_input') {
        value = String(answer) + '/' + ((page.sliderConfig || {}).maxValue || 6);
      } else if (page.questionType === 'email_input') {
        value = String(answer);
      } else {
        value = asArray(answer).map(function (k) { return labelFor(page.id, k); }).join(', ');
      }
      if (value) attrs['Quiz: ' + page.title] = value;
    });
    if (state.outcome) attrs['Quiz result'] = state.outcome;
    return attrs;
  }

  function updateCartAttributes() {
    return fetch(CONF.routes && CONF.routes.cart_update ? CONF.routes.cart_update : '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: answerAttributes() })
    }).catch(function () { /* attributes are a nicety, never block the shopper */ });
  }

  // Uses the theme's own {% form 'customer' %} so the shopper lands in Shopify's
  // customer list with marketing consent. No third-party email app is installed.
  function submitEmail(email) {
    var form = document.getElementById('hair-quiz-signup');
    if (!form) return Promise.resolve(false);
    var field = form.querySelector('input[type="email"]');
    if (field) field.value = email;
    var body = new FormData(form);
    return fetch(form.getAttribute('action') || '/contact', {
      method: 'POST',
      body: body,
      headers: { Accept: 'text/html' }
    }).then(function (res) { return res.ok; })
      .catch(function () { return false; });
  }

  function addToCart(variantId, quantity, button) {
    if (!variantId) return Promise.resolve(false);
    var original = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = t('adding');
    }
    return updateCartAttributes().then(function () {
      return fetch(CONF.routes && CONF.routes.cart_add ? CONF.routes.cart_add : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
      });
    }).then(function (res) {
      if (!res.ok) throw new Error('add failed');
      if (button) {
        button.disabled = false;
        button.textContent = t('added');
      }
      return true;
    }).catch(function () {
      if (button) {
        button.disabled = false;
        button.textContent = original;
      }
      showError(t('cart_error'));
      return false;
    });
  }

  function money(cents) {
    if (cents == null) return '';
    var format = CONF.moneyFormat || '{{amount}}';
    var amount = (cents / 100).toFixed(2);
    if (format.indexOf('{{amount_no_decimals}}') !== -1) {
      return format.replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
    }
    return format.replace('{{amount}}', amount).replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  /* ---------- chrome ---------- */

  function renderChrome() {
    var page = BY_ID[state.current];
    var bar = root.querySelector('.hq-progress-fill');
    if (bar) bar.style.width = progressPercent() + '%';

    var backBtn = root.querySelector('.hq-back');
    if (backBtn) {
      var canGoBack = CFG.allowBackNavigation !== false &&
        state.history.length > 0 &&
        page.type !== 'smart_buffer';
      backBtn.hidden = !canGoBack;
    }

    var counter = root.querySelector('.hq-step');
    if (counter) {
      var n = questionNumber(page);
      if (n) {
        counter.textContent = t('step', { current: n, total: COUNTED.length });
        counter.hidden = false;
      } else {
        counter.hidden = true;
      }
    }
  }

  /* ---------- page heads and options ---------- */

  function headFor(page) {
    var wrap = el('div', 'hq-head');
    wrap.appendChild(el('h1', 'hq-title', esc(page.title || '')));
    if (page.subtitle) wrap.appendChild(el('p', 'hq-subtitle', esc(page.subtitle)));
    if (page.helpText) {
      var details = el('details', 'hq-why');
      var summary = el('summary', 'hq-why-summary', esc(t('why_asking')));
      details.appendChild(summary);
      details.appendChild(el('div', 'hq-why-body', page.helpText));
      wrap.appendChild(details);
    }
    return wrap;
  }

  function optionList(page) {
    var multiple = page.questionType === 'multiple_choice';
    var list = el('div', 'hq-options');
    var selected = asArray(state.answers[page.id]);

    (page.options || []).forEach(function (option) {
      var isOn = selected.indexOf(option.key) !== -1;
      var button = el('button', 'hq-option' + (isOn ? ' is-selected' : ''));
      button.type = 'button';
      button.setAttribute('role', multiple ? 'checkbox' : 'radio');
      button.setAttribute('aria-checked', isOn ? 'true' : 'false');
      button.innerHTML =
        '<span class="hq-option-box" aria-hidden="true"></span>' +
        '<span class="hq-option-text">' + esc(option.text) + '</span>';

      button.addEventListener('click', function () {
        if (multiple) {
          toggleMultiple(page, option, button);
        } else {
          pickSingle(page, option);
        }
      });

      list.appendChild(button);
    });

    return list;
  }

  function toggleMultiple(page, option, button) {
    var current = asArray(state.answers[page.id]).slice();
    var index = current.indexOf(option.key);
    var exclusive = isExclusive(page, option.key);

    if (index === -1) {
      // "None of these" style answers clear everything else, and vice versa.
      current = exclusive ? [option.key] : current.filter(function (k) {
        return !isExclusive(page, k);
      }).concat([option.key]);
    } else {
      current.splice(index, 1);
    }

    state.answers[page.id] = current;
    save();
    showError('');
    renderChrome();
    refreshOptions(page);
    if (button) button.blur();
  }

  // Keys whose option id reads as an opt-out. Data driven so no key list is hard-coded.
  function isExclusive(page, key) {
    var options = page.options || [];
    for (var i = 0; i < options.length; i++) {
      if (options[i].key !== key) continue;
      return /^(no|none|neither|no_thanks|prefer_not_say)$/.test(options[i].id || '');
    }
    return false;
  }

  function refreshOptions(page) {
    var selected = asArray(state.answers[page.id]);
    var buttons = root.querySelectorAll('.hq-option');
    (page.options || []).forEach(function (option, i) {
      var button = buttons[i];
      if (!button) return;
      var isOn = selected.indexOf(option.key) !== -1;
      button.classList.toggle('is-selected', isOn);
      button.setAttribute('aria-checked', isOn ? 'true' : 'false');
    });
  }

  function pickSingle(page, option) {
    state.answers[page.id] = [option.key];
    save();
    showError('');
    refreshOptions(page);
    if (CFG.autoContinue === false) {
      renderChrome();
      return;
    }
    var note = root.querySelector('.hq-auto');
    if (note) {
      note.textContent = t('auto_continue');
      note.hidden = false;
    }
    setTimeout(goNext, CFG.autoContinueDelay || 420);
  }

  function primaryButton(label, onClick) {
    var button = el('button', 'hq-btn hq-btn--primary', esc(label));
    button.type = 'button';
    button.addEventListener('click', onClick);
    return button;
  }

  /* ---------- screens ---------- */

  function screenChoice(page) {
    var frag = document.createDocumentFragment();
    frag.appendChild(headFor(page));
    frag.appendChild(optionList(page));
    if (page.questionType === 'single_choice') {
      frag.appendChild(el('p', 'hq-auto', ''));
      frag.lastChild.hidden = true;
    }
    if (page.questionType === 'multiple_choice') {
      frag.appendChild(primaryButton(page.submitButtonText || t('continue'), attemptNext));
    }
    return frag;
  }

  function screenDate(page) {
    var frag = document.createDocumentFragment();
    frag.appendChild(headFor(page));
    var saved = state.answers[page.id] || {};
    var row = el('div', 'hq-date');

    [
      { name: 'day', label: t('day'), min: 1, max: 31, size: 2 },
      { name: 'month', label: t('month'), min: 1, max: 12, size: 2 },
      { name: 'year', label: t('year'), min: 1900, max: new Date().getFullYear(), size: 4 }
    ].forEach(function (field) {
      var wrap = el('label', 'hq-date-field');
      wrap.appendChild(el('span', 'hq-date-label', esc(field.label)));
      var input = document.createElement('input');
      input.type = 'text';
      input.inputMode = 'numeric';
      input.autocomplete = 'off';
      input.className = 'hq-input hq-input--date';
      input.maxLength = field.size;
      input.placeholder = field.size === 4 ? '1990' : '01';
      input.value = saved[field.name] || '';
      input.addEventListener('input', function () {
        input.value = input.value.replace(/[^0-9]/g, '');
        var next = state.answers[page.id] || {};
        next[field.name] = input.value;
        state.answers[page.id] = next;
        save();
      });
      wrap.appendChild(input);
      row.appendChild(wrap);
    });

    frag.appendChild(row);
    frag.appendChild(primaryButton(page.submitButtonText || t('continue'), attemptNext));
    return frag;
  }

  function screenSlider(page) {
    var frag = document.createDocumentFragment();
    frag.appendChild(headFor(page));

    var conf = page.sliderConfig || { minValue: 0, maxValue: 6 };
    var value = state.answers[page.id];
    if (value == null) {
      value = page.defaultValue == null ? Math.round((conf.minValue + conf.maxValue) / 2) : page.defaultValue;
      state.answers[page.id] = value;
    }

    var wrap = el('div', 'hq-slider');
    var input = document.createElement('input');
    input.type = 'range';
    input.className = 'hq-range';
    input.min = String(conf.minValue);
    input.max = String(conf.maxValue);
    input.step = '1';
    input.value = String(value);
    input.setAttribute('aria-label', page.title || '');

    var ticks = el('div', 'hq-range-ticks');
    for (var i = conf.minValue; i <= conf.maxValue; i++) {
      ticks.appendChild(el('span', 'hq-range-tick', ''));
    }

    var labels = el('div', 'hq-range-labels');
    labels.appendChild(el('span', 'hq-range-min', esc(conf.minLabel || '')));
    labels.appendChild(el('span', 'hq-range-max', esc(conf.maxLabel || '')));

    function paint() {
      var span = conf.maxValue - conf.minValue || 1;
      var pct = ((Number(input.value) - conf.minValue) / span) * 100;
      input.style.setProperty('--hq-range-pct', pct + '%');
    }

    input.addEventListener('input', function () {
      state.answers[page.id] = Number(input.value);
      save();
      paint();
    });

    wrap.appendChild(input);
    wrap.appendChild(ticks);
    wrap.appendChild(labels);
    frag.appendChild(wrap);
    frag.appendChild(primaryButton(page.submitButtonText || t('continue'), attemptNext));
    paint();
    return frag;
  }

  var HAIR = {
    shape: [
      { key: 'straight', text: 'Straight' },
      { key: 'wavy', text: 'Wavy' },
      { key: 'curly', text: 'Curly' },
      { key: 'coily', text: 'Coily' }
    ],
    consistency: [
      { key: 'thin_brittle', text: 'Thin and brittle' },
      { key: 'medium', text: 'Somewhere in between' },
      { key: 'thick_strong', text: 'Thick and strong' }
    ],
    length: [
      { key: 'short', text: 'Short' },
      { key: 'medium', text: 'Medium' },
      { key: 'long', text: 'Long' }
    ]
  };

  function screenHair(page) {
    var frag = document.createDocumentFragment();
    frag.appendChild(headFor(page));
    var saved = state.answers[page.id] || {};
    var summary = el('p', 'hq-hair-summary', '');

    function paintSummary() {
      var current = state.answers[page.id] || {};
      if (!current.shape || !current.consistency || !current.length) {
        summary.textContent = '';
        return;
      }
      var words = [current.length, current.consistency, current.shape].map(function (key) {
        var all = HAIR.shape.concat(HAIR.consistency, HAIR.length);
        for (var i = 0; i < all.length; i++) {
          if (all[i].key === key) return all[i].text.toLowerCase();
        }
        return key;
      });
      summary.textContent = t('you_have') + ' ' + words.join(', ') + ' ' + t('hair') + '.';
    }

    ['shape', 'consistency', 'length'].forEach(function (facet) {
      var group = el('fieldset', 'hq-hair-group');
      group.appendChild(el('legend', 'hq-hair-legend', esc(t(facet))));
      var row = el('div', 'hq-chips');
      HAIR[facet].forEach(function (choice) {
        var chip = el('button', 'hq-chip' + (saved[facet] === choice.key ? ' is-selected' : ''), esc(choice.text));
        chip.type = 'button';
        chip.setAttribute('aria-pressed', saved[facet] === choice.key ? 'true' : 'false');
        chip.addEventListener('click', function () {
          var next = state.answers[page.id] || {};
          next[facet] = choice.key;
          state.answers[page.id] = next;
          save();
          showError('');
          Array.prototype.forEach.call(row.children, function (node) {
            node.classList.remove('is-selected');
            node.setAttribute('aria-pressed', 'false');
          });
          chip.classList.add('is-selected');
          chip.setAttribute('aria-pressed', 'true');
          paintSummary();
        });
        row.appendChild(chip);
      });
      group.appendChild(row);
      frag.appendChild(group);
    });

    frag.appendChild(summary);
    frag.appendChild(primaryButton(page.submitButtonText || t('continue'), attemptNext));
    paintSummary();
    return frag;
  }

  function screenEmail(page) {
    var frag = document.createDocumentFragment();
    frag.appendChild(headFor(page));

    var field = el('label', 'hq-field');
    field.appendChild(el('span', 'hq-field-label', esc(t('email_label'))));
    var input = document.createElement('input');
    input.type = 'email';
    input.className = 'hq-input';
    input.autocomplete = 'email';
    input.placeholder = page.emailPlaceholder || '';
    input.value = state.answers[page.id] || '';
    input.addEventListener('input', function () {
      state.answers[page.id] = input.value;
      save();
    });
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        submit();
      }
    });
    field.appendChild(input);
    frag.appendChild(field);

    if (page.consentText) {
      frag.appendChild(el('div', 'hq-consent', page.consentText));
    }

    var button = primaryButton(page.submitButtonText || t('continue'), submit);
    frag.appendChild(button);

    function submit() {
      var problem = validate(page);
      if (problem) {
        showError(problem);
        return;
      }
      showError('');
      state.email = String(state.answers[page.id]).trim();
      state.outcome = decideOutcome();
      save();
      button.disabled = true;
      submitEmail(state.email).then(function () {
        updateCartAttributes();
        button.disabled = false;
        goNext();
      });
    }

    return frag;
  }

  function screenContent(page) {
    var frag = document.createDocumentFragment();
    frag.appendChild(headFor(page));
    if (page.bullets && page.bullets.length) {
      var list = el('ol', 'hq-bullets');
      page.bullets.forEach(function (item) {
        var li = el('li', 'hq-bullet');
        li.appendChild(el('h2', 'hq-bullet-title', esc(item.title)));
        li.appendChild(el('p', 'hq-bullet-text', esc(item.text)));
        list.appendChild(li);
      });
      frag.appendChild(list);
    }
    frag.appendChild(primaryButton(page.buttonText || t('continue'), goNext));
    return frag;
  }

  // Rotates the shopper's own answers back at them while the outcome is decided.
  // No fake loading time beyond the rotation the reference also uses.
  var bufferTimers = [];

  function clearBufferTimers() {
    bufferTimers.forEach(clearTimeout);
    bufferTimers.forEach(clearInterval);
    bufferTimers = [];
  }

  function screenBuffer(page) {
    var frag = document.createDocumentFragment();
    state.outcome = decideOutcome();
    save();

    frag.appendChild(headFor(page));

    var insights = buildInsights();
    var stage = el('div', 'hq-buffer');
    stage.setAttribute('aria-live', 'polite');
    var spinner = el('div', 'hq-spinner', '');
    spinner.setAttribute('aria-hidden', 'true');
    stage.appendChild(spinner);

    var line = el('div', 'hq-buffer-line', '');
    stage.appendChild(line);
    frag.appendChild(stage);

    var index = 0;
    function paint() {
      if (!insights.length) return;
      var item = insights[index % insights.length];
      line.innerHTML =
        '<p class="hq-buffer-title">' + esc(item.title) + '</p>' +
        (item.body ? '<p class="hq-buffer-body">' + esc(item.body) + '</p>' : '');
      index += 1;
    }
    paint();

    bufferTimers.push(setInterval(paint, page.rotationDelay || 2600));
    bufferTimers.push(setTimeout(function () {
      clearBufferTimers();
      goTo(CFG.resultPage || 'result_page', false);
    }, page.autoAdvanceDelay || 9000));

    var skip = el('button', 'hq-btn hq-btn--ghost', esc(t('skip')));
    skip.type = 'button';
    skip.addEventListener('click', function () {
      clearBufferTimers();
      goTo(CFG.resultPage || 'result_page', false);
    });
    frag.appendChild(skip);

    return frag;
  }

  function productCard(product, buttonLabel) {
    var card = el('div', 'hq-product');
    if (product.image) {
      // The desktop two-column grid only makes sense with a picture, so flag it here
      // rather than letting an imageless card sit in a 200px media column.
      card.className = 'hq-product hq-product--media';
      card.innerHTML = '<div class="hq-product-media">' +
        '<img src="' + esc(product.image) + '" alt="' + esc(product.title) + '" width="480" height="480" loading="lazy">' +
        '</div>';
    }
    var body = el('div', 'hq-product-body');
    body.appendChild(el('h3', 'hq-product-title', esc(product.title)));
    if (product.subtitle) body.appendChild(el('p', 'hq-product-sub', esc(product.subtitle)));
    if (product.price != null) body.appendChild(el('p', 'hq-product-price', esc(money(product.price))));

    if (product.available && product.variantId) {
      var add = primaryButton(buttonLabel || t('add_to_cart'), function () {
        addToCart(product.variantId, 1, add).then(function (ok) {
          if (ok) goTo(nextAfterResult(), true);
        });
      });
      body.appendChild(add);
    } else if (product.url) {
      var link = el('a', 'hq-btn hq-btn--primary', esc(t('continue')));
      link.href = product.url;
      body.appendChild(link);
    }

    if (product.url) {
      var more = el('a', 'hq-product-link', esc('See the full details'));
      more.href = product.url;
      body.appendChild(more);
    }

    card.appendChild(body);
    return card;
  }

  function nextAfterResult() {
    if (CONF.upsell && BY_ID.upsell_page) return 'upsell_page';
    return BY_ID.checkout_page ? 'checkout_page' : (CFG.resultPage || 'result_page');
  }

  function screenResult(page) {
    var frag = document.createDocumentFragment();
    if (!state.outcome) state.outcome = decideOutcome();
    var outcome = (DATA.outcomes || {})[state.outcome] || {};
    var showProduct = outcome.showProduct && !state.flags.no_product;

    frag.appendChild(headFor(page));

    var box = el('div', 'hq-outcome');
    box.appendChild(el('h2', 'hq-outcome-title', esc(outcome.title || '')));
    if (outcome.reasoning) box.appendChild(el('p', 'hq-outcome-text', esc(outcome.reasoning)));
    if (outcome.warning) box.appendChild(el('p', 'hq-outcome-warning', esc(outcome.warning)));
    frag.appendChild(box);

    if (showProduct) {
      if (CONF.product) {
        frag.appendChild(productCard(CONF.product, t('add_to_cart')));
      } else if (CONF.fallback && CONF.fallback.url) {
        // No product is linked to this section yet, so send the shopper somewhere real
        // rather than showing a dead button.
        var note = el('div', 'hq-fallback');
        if (CONF.fallback.text) note.appendChild(el('p', 'hq-fallback-text', esc(CONF.fallback.text)));
        var link = el('a', 'hq-btn hq-btn--primary', esc(CONF.fallback.label || t('continue')));
        link.href = CONF.fallback.url;
        note.appendChild(link);
        frag.appendChild(note);
      }
    }

    var insights = buildInsights();
    if (insights.length) {
      var reasons = el('div', 'hq-reasons');
      reasons.appendChild(el('h2', 'hq-reasons-title', esc(t('reasons_title'))));
      var list = el('ul', 'hq-reasons-list');
      insights.forEach(function (item) {
        var li = el('li', 'hq-reason');
        li.appendChild(el('span', 'hq-reason-title', esc(item.title)));
        if (item.body) li.appendChild(el('span', 'hq-reason-body', esc(item.body)));
        list.appendChild(li);
      });
      reasons.appendChild(list);
      frag.appendChild(reasons);
    }

    var restart = el('button', 'hq-btn hq-btn--ghost', esc(t('restart')));
    restart.type = 'button';
    restart.addEventListener('click', reset);
    frag.appendChild(restart);

    state.done = true;
    save();
    return frag;
  }

  function screenUpsell(page) {
    var frag = document.createDocumentFragment();
    frag.appendChild(headFor(page));
    if (CONF.upsell) {
      frag.appendChild(productCard(CONF.upsell, t('add_to_cart')));
    }
    var skip = el('button', 'hq-btn hq-btn--ghost', esc(t('no_thanks')));
    skip.type = 'button';
    skip.addEventListener('click', function () {
      goTo(BY_ID.checkout_page ? 'checkout_page' : (CFG.resultPage || 'result_page'));
    });
    frag.appendChild(skip);
    return frag;
  }

  function screenCheckout(page) {
    var frag = document.createDocumentFragment();
    frag.appendChild(headFor(page));

    var summary = el('div', 'hq-cart', '<p class="hq-cart-loading">' + esc(t('adding')) + '</p>');
    frag.appendChild(summary);

    fetch(CONF.routes && CONF.routes.cart ? CONF.routes.cart + '.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    }).then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) {
          summary.innerHTML = '<p class="hq-cart-empty">' + esc(t('cart_error')) + '</p>';
          return;
        }
        var rows = cart.items.map(function (item) {
          return '<li class="hq-cart-row">' +
            '<span class="hq-cart-name">' + esc(item.product_title) + ' &times; ' + item.quantity + '</span>' +
            '<span class="hq-cart-price">' + esc(money(item.final_line_price)) + '</span>' +
            '</li>';
        }).join('');
        summary.innerHTML = '<ul class="hq-cart-list">' + rows + '</ul>' +
          '<p class="hq-cart-total"><span>' + esc(t('checkout')) + '</span>' +
          '<strong>' + esc(money(cart.total_price)) + '</strong></p>';
      })
      .catch(function () {
        summary.innerHTML = '<p class="hq-cart-empty">' + esc(t('cart_error')) + '</p>';
      });

    var go = el('a', 'hq-btn hq-btn--primary', esc(t('checkout')));
    go.href = '/checkout';
    frag.appendChild(go);
    return frag;
  }

  /* ---------- render ---------- */

  var SCREENS = {
    multiple_choice: screenChoice,
    single_choice: screenChoice,
    date_input: screenDate,
    slider_input: screenSlider,
    hair_description: screenHair,
    email_input: screenEmail
  };

  function bodyFor(page) {
    if (page.type === 'content') return screenContent(page);
    if (page.type === 'smart_buffer') return screenBuffer(page);
    if (page.type === 'result') return screenResult(page);
    if (page.type === 'upsell') return screenUpsell(page);
    if (page.type === 'checkout') return screenCheckout(page);
    var build = SCREENS[page.questionType];
    return build ? build(page) : screenContent(page);
  }

  function render() {
    clearBufferTimers();
    var page = BY_ID[state.current];
    if (!page) return;

    var stage = root.querySelector('.hq-stage');
    if (!stage) return;
    stage.innerHTML = '';
    stage.setAttribute('data-page', page.id);
    stage.setAttribute('data-type', page.type === 'question' ? page.questionType : page.type);
    stage.appendChild(bodyFor(page));

    var live = el('p', 'hq-error-live');
    live.setAttribute('role', 'alert');
    live.hidden = true;
    stage.appendChild(live);

    renderChrome();

    // Move focus to the heading so keyboard and screen reader users land on the new question.
    var heading = stage.querySelector('.hq-title');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  }

  function reset() {
    clearSaved();
    clearBufferTimers();
    state.answers = {};
    state.history = [];
    state.flags = {};
    state.email = '';
    state.outcome = null;
    state.done = false;
    state.current = CFG.firstPage || PAGES[0].id;
    render();
  }

  /* ---------- boot ---------- */

  function bindChrome() {
    var backBtn = root.querySelector('.hq-back');
    if (backBtn) backBtn.addEventListener('click', goBack);

    var exitBtn = root.querySelector('.hq-exit');
    if (exitBtn && !exitBtn.getAttribute('href')) {
      exitBtn.addEventListener('click', function () { window.history.back(); });
    }
  }

  // A finished or half-finished quiz is offered back rather than resumed silently,
  // because landing mid-questionnaire with no explanation is disorienting.
  function offerResume(saved) {
    var stage = root.querySelector('.hq-stage');
    if (!stage) return false;

    var answered = Object.keys(saved.answers || {}).length;
    if (!answered) return false;

    stage.innerHTML = '';
    var box = el('div', 'hq-resume');
    box.appendChild(el('h1', 'hq-title', esc(t('resume_title'))));

    // Tell them how far they got, from the saved page, not from the fresh state.
    var savedPage = BY_ID[saved.current];
    var savedNumber = savedPage ? questionNumber(savedPage) : null;
    if (savedNumber) {
      box.appendChild(el('p', 'hq-subtitle', esc(t('resume_body', {
        current: savedNumber,
        total: COUNTED.length
      }))));
    }

    var row = el('div', 'hq-resume-actions');
    var yes = primaryButton(t('resume_yes'), function () {
      state.current = saved.current;
      state.answers = saved.answers || {};
      state.history = saved.history || [];
      state.flags = saved.flags || {};
      state.email = saved.email || '';
      state.outcome = saved.outcome || null;
      state.done = !!saved.done;
      render();
    });
    var no = el('button', 'hq-btn hq-btn--ghost', esc(t('resume_no')));
    no.type = 'button';
    no.addEventListener('click', reset);

    row.appendChild(yes);
    row.appendChild(no);
    box.appendChild(row);
    stage.appendChild(box);
    renderChrome();

    // The resume screen is not a question, so the counter would be showing the number
    // of the page they have not resumed to yet.
    var counter = root.querySelector('.hq-step');
    if (counter) counter.hidden = true;
    return true;
  }

  bindChrome();
  root.classList.add('is-ready');

  var saved = load();
  if (!saved || !offerResume(saved)) {
    render();
  }
})();
